import { colSettings } from "../../../common/components/table/agGrid/colSettings";
import { agentProductionTerms as apt } from "../../../constants";
import { agentsColumns, listingProficiencyColTitles, } from "../../constants/agentProductionConstants";
import OfficeNameLink from "../components/cellRenderers/OfficeNameLink";
import { terms, tooltips as tips } from "../constants";

export const additionalTableHeaderIds = Object.freeze({
    officeInformation: 'officeInformation',
    agents: 'agents',
    potentialImpact: 'potentialImpact',
    agentInformation: 'agentInformation',
    beforeMove: 'beforeMove',
    afterMove: 'afterMove',
});

export const colIds = Object.freeze({
    officeStreetName: 'officeStreetName',
    agentsLost: 'agentsLost',
    agentsGained: 'agentsGained',
    netAgents: 'netAgents',
    netVolume: 'netVolume',
});

export const parentMetrics = Object.freeze({
    officeInformation: {
        id: additionalTableHeaderIds.officeInformation,
        label: listingProficiencyColTitles.officeInformation
    },
    agents: {
        id: additionalTableHeaderIds.agents,
        label: listingProficiencyColTitles.agents
    },
    potentialImpact: {
        id: additionalTableHeaderIds.potentialImpact,
        label: listingProficiencyColTitles.potentialImpact
    },
});

export const officeInformationParentColumns = Object.freeze({
    officeName: {
        ...agentsColumns.officeName,
        tip: tips.officeName,
        isDisplayed: true,
    },
    officeId: {
        ...agentsColumns.officeId,
        tip: tips.officeId,
        metrics: { ...parentMetrics.officeInformation },
        isDisplayed: false,
    },
    officeStreetName: {
        id: colIds.officeStreetName,
        label: apt.officeAddress,
        tip: tips.officeAddress,
        metrics: { ...parentMetrics.officeInformation },
        isDisplayed: false,
    },
    officeCity: {
        ...agentsColumns.officeCity,
        tip: tips.city,
        isDisplayed: false,
        boundMetricId: colIds.officeStreetName,
    },
    officeZipCode: {
        ...agentsColumns.officeZipCode,
        tip: tips.zip,
        isDisplayed: false,
        boundMetricId: colIds.officeStreetName,
    },
});

export const agentsParentColumns = Object.freeze({
    agentsLost: {
        id: colIds.agentsLost,
        label: terms.lostAgentsTo,
        metricLabel: terms.lost,
        tip: tips.lostAgentsTo,
        metrics: { ...parentMetrics.agents },
        isDisplayed: true,
    },
    agentsGained: {
        id: colIds.agentsGained,
        label: terms.gainedAgentsFrom,
        metricLabel: terms.gained,
        tip: tips.gainedAgentsFrom,
        metrics: { ...parentMetrics.agents },
        isDisplayed: true,
    },
    netAgents: {
        id: colIds.netAgents,
        label: terms.netAgentsGained,
        metricLabel: terms.netGained,
        tip: tips.netAgentsGained,
        metrics: { ...parentMetrics.agents },
        isDisplayed: true,
    },
});

export const potentialImpactParentColumns = Object.freeze({
    netVolume: {
        id: colIds.netVolume,
        label: terms.netDollarVolume,
        tip: tips.netDollarVolume,
        metrics: { ...parentMetrics.potentialImpact },
        isDisplayed: true,
    },
});

export const columns = Object.freeze({
    ...officeInformationParentColumns,
    ...agentsParentColumns,
    ...potentialImpactParentColumns,
});

export const officeBreakdownAgentsColumns = Object.freeze({
    agentsGained: {
        ...agentsParentColumns.agentsGained,
        label: terms.agentsGained,
        tip: tips.agentsGained
    },
    agentsLost: {
        ...agentsParentColumns.agentsLost,
        label: terms.agentsLost,
        tip: tips.agentsLost
    },
    netAgents: {
        ...agentsParentColumns.netAgents
    }
})

export const officeBreakdownColumns = Object.freeze({
    ...officeInformationParentColumns,
    ...officeBreakdownAgentsColumns,
    ...potentialImpactParentColumns
});


export const officeNameColumn = {
    col: officeInformationParentColumns.officeName,
    colDef: colSettings.textLg,
    cellRenderer: OfficeNameLink
};

export const officeInformationArrayColumns = shouldShowAdditionalInfo => [
    {
        col: {
            ...officeInformationParentColumns.officeId,
            isDisplayed: shouldShowAdditionalInfo,
        },
        colDef: colSettings.textSm,
    },
    {
        col: {
            ...officeInformationParentColumns.officeStreetName,
            isDisplayed: shouldShowAdditionalInfo,
        },
        colDef: colSettings.textMd,
    },
    {
        col: {
            ...officeInformationParentColumns.officeCity,
            isDisplayed: shouldShowAdditionalInfo,
        },
        colDef: colSettings.textSm,
    },
    {
        col: {
            ...officeInformationParentColumns.officeZipCode,
            isDisplayed: shouldShowAdditionalInfo,
        },
        colDef: colSettings.textXs,
    },
];

export const officeInformationColumnsOfficeBreakdown = shouldShowAdditionalInfo => [
    officeNameColumn,
    ...officeInformationArrayColumns(shouldShowAdditionalInfo),
];

export const agentsArrayColumns = [
    {
        col: agentsParentColumns.agentsLost,
        colDef: colSettings.numberSmInteger,
    },
    {
        col: agentsParentColumns.agentsGained,
        colDef: colSettings.numberMdInteger,
    },
    {
        col: agentsParentColumns.netAgents,
        colDef: colSettings.numberMdInteger,
    },
];

export const agentsColumnsOfficeBreakdown = [
    {
        col: {...agentsParentColumns.agentsGained, label: terms.agentsGained},
        colDef: colSettings.numberMdInteger,
    },
    {
        col: {...agentsParentColumns.agentsLost, label: terms.agentsLost},
        colDef: colSettings.numberSmInteger,
    },
    {
        col: agentsParentColumns.netAgents,
        colDef: colSettings.numberMdInteger,
    },
];


export const potentialImpactArrayColumns = [{
    col: potentialImpactParentColumns.netVolume,
    colDef: colSettings.numberSmInteger,
}];

export const additionalTableHeaders = [
    {
        [additionalTableHeaderIds.officeInformation]: {
            title: listingProficiencyColTitles.officeInformation,
            attrs: {
                colSpan: 5,
                clasName: 'border text-center'
            }
         },
        [additionalTableHeaderIds.agents]: {
            title: listingProficiencyColTitles.agents,
            attrs: {
                colSpan: 3,
                clasName: 'border text-center'
            }
        },
        [additionalTableHeaderIds.potentialImpact]: {
            title: listingProficiencyColTitles.potentialImpact,
            attrs: {
                colSpan: 1,
                clasName: 'border text-center'
            }
        }
    }
];

export const breakdownAdditionalHeaders = [
    {
        [additionalTableHeaderIds.agentInformation]: {
            title: listingProficiencyColTitles.agentInformation,
            attrs: {
                colSpan: 14
            }
        },
        [additionalTableHeaderIds.beforeMove]: {
            title: listingProficiencyColTitles.beforeMove,
            attrs: {
                colSpan: 1
            }
        },
        [additionalTableHeaderIds.afterMove]: {
            title: listingProficiencyColTitles.afterMove,
            attrs: {
                colSpan: 6
            }
        }
    }
];
